import React, { useRef } from 'react';
import Spinner from '@atlaskit/spinner';
import ToolTip from '@atlaskit/tooltip';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics/src/index.tsx';
import { softwareGenericLongTasksReporter } from '@atlassian/jira-software-browser-metrics/src/services/reporters/software-generic-long-tasks/index.tsx';
import { useEvent } from '@atlassian/jira-software-react-use-event/src/index.tsx';
import { useShowCompactMode } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { BACKLOG_LONG_TASKS_DND_MARK_NAME } from '../../../model/constants.tsx';
import type { CardId } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getCards, hasCardExtraFields } from '../../../state/selectors/cards/index.tsx';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities.tsx';
import CardContents from './card-contents/index.tsx';
import { CARD_CONTAINER_SELECTOR } from './constants.tsx';
import { DropIndicator } from './drop-indicator/index.tsx';
import {
	CardContainer,
	ContentContainer,
	ImageContainer,
	Img,
	StyledSpinner,
	Summary,
	SummaryAndParentContainer,
} from './styled.tsx';
import { useDraggableCard } from './use-draggable-card/index.tsx';

export type Props = {
	id: CardId;
	cardListId: string;
	canShowStatus?: boolean;
	draggableIndex?: number;
	shouldRenderRichCard?: boolean;
	isAttachedToParent?: boolean;
	subtaskIds?: CardId[] | undefined;
};

export const Card = ({
	id,
	cardListId,
	canShowStatus = false,
	shouldRenderRichCard = true,
	isAttachedToParent = false,
	subtaskIds = undefined,
}: Props) => {
	const card = useBacklogSelector((state) => getCards(state)[String(id)]);
	const isCompanyManaged = useIsCMPBacklog();
	const showCompactMode = useShowCompactMode();

	const onMouseDown = useEvent(() => {
		// Clear any text selection to prevent unexpected dragging behaviour

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.getSelection()?.removeAllRanges();

		getLongTasksMetrics('dnd').start('backlog', BACKLOG_LONG_TASKS_DND_MARK_NAME);
	});
	const onMouseUp = useEvent(() => {
		getLongTasksMetrics('dnd').stop(softwareGenericLongTasksReporter('dnd'));
	});
	const getHasExtraFields = useBacklogSelector(hasCardExtraFields);

	const elementRef = useRef<HTMLDivElement>(null);
	const { isDragging, dragPreview, closestEdge, dropState } = useDraggableCard({
		elementRef,
		cardListId,
		cardId: id,
	});

	if (!card) {
		return null;
	}

	const key = card.key;
	const summary = card.summary;
	const icon = card.icon;

	const isOptimistic = id < 0;
	if (isOptimistic) {
		return (
			<CardContainer
				isCompact={showCompactMode}
				lowerOpacity={false}
				isSelected={false}
				isFlagged={false}
				isDraggable
				isDragHandle={false}
				hasExtraFields={getHasExtraFields}
				isOptimistic
				shouldShowChevron={isCompanyManaged}
				data-component-selector={CARD_CONTAINER_SELECTOR}
			>
				<ImageContainer>
					<Img role="presentation" src={icon} />
				</ImageContainer>

				<SummaryAndParentContainer>
					<ToolTip content={summary}>
						<Summary>{summary}</Summary>
					</ToolTip>
				</SummaryAndParentContainer>

				<StyledSpinner>
					<Spinner size="small" />
				</StyledSpinner>
			</CardContainer>
		);
	}

	const testIdProps = {
		'data-test-id': `software-backlog.card-list.card.content-container.${key}`,
		'data-testid': `software-backlog.card-list.card.content-container.${key}`,
	};

	return (
		<ContentContainer
			ref={elementRef}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			tabIndex={-1}
			{...testIdProps}
		>
			<CardContents
				id={id}
				canShowStatus={canShowStatus}
				isDragging={isDragging}
				shouldRenderRichCard={shouldRenderRichCard}
				isAttachedToParent={isAttachedToParent}
				subtaskIds={subtaskIds}
			/>

			{closestEdge && (
				<DropIndicator
					dropStatisticsCardListId={dropState?.cardListIfDivider ?? undefined}
					droppingOverCardListId={cardListId}
					droppingOverCardId={id}
					edge={closestEdge}
				/>
			)}

			{dragPreview}
		</ContentContainer>
	);
};

export default Card;
